<template lang="pug">
    .reservation-form
        el-page-header(@back="goBack" :content="isEditing ? $t('Edit Reservation') : $t('New Reservation')")

        el-card(style="margin-top: 20px")
            el-form(ref="form" :model="form" :rules="rules" label-width="180px" label-position="top")
                el-row(:gutter="30")
                    el-col(:span="12")
                        h3 {{ $t('Guest Information') }}

                        el-form-item(:label="$t('Guest Name')" prop="guestName")
                            .input-with-keyboard
                                el-input(
                                    ref="guestNameInput"
                                    v-model="form.guestName"
                                    :placeholder="$t('Guest Name')"
                                    :disabled="!canEdit"
                                    @focus="onFieldFocus('guestName')"
                                )
                                button.keyboard-toggle(
                                    v-if="canEdit"
                                    type="button"
                                    :class="{ active: activeKeyboardField === 'guestName' }"
                                    :title="$t('Show on-screen keyboard')"
                                    @click.prevent.stop="toggleKeyboard('guestName')"
                                )
                                    i.el-icon-fa-keyboard.far
                            transition(name="slide-decent-vertical")
                                .keyboard-container(v-if="activeKeyboardField === 'guestName'")
                                    keyboard(
                                        ref="keyboardPanel"
                                        v-model="activeKeyboardValue"
                                        :config="keyboardConfig"
                                        @key-press="onKeyboardKeyPress"
                                    )

                        el-form-item(:label="$t('Phone')" prop="guestPhone")
                            .input-with-keyboard
                                el-input(v-model="form.guestPhone" :placeholder="$t('Phone')" :disabled="!canEdit" @focus="onFieldFocus('guestPhone')")
                                button.keyboard-toggle(
                                    v-if="canEdit"
                                    type="button"
                                    :class="{ active: activeKeyboardField === 'guestPhone' }"
                                    :title="$t('Show on-screen keyboard')"
                                    @click.prevent.stop="toggleKeyboard('guestPhone')"
                                )
                                    i.el-icon-fa-keyboard.far
                            transition(name="slide-decent-vertical")
                                .keyboard-container.keyboard-container--numeric(v-if="activeKeyboardField === 'guestPhone'")
                                    keyboard(
                                        ref="keyboardPanel"
                                        v-model="activeKeyboardValue"
                                        :config="numericKeyboardConfig"
                                        @key-press="onKeyboardKeyPress"
                                    )

                        el-form-item(:label="$t('Email')" prop="guestEmail")
                            .input-with-keyboard
                                el-input(v-model="form.guestEmail" :placeholder="$t('Email')" type="email" :disabled="!canEdit" @focus="onFieldFocus('guestEmail')")
                                button.keyboard-toggle(
                                    v-if="canEdit"
                                    type="button"
                                    :class="{ active: activeKeyboardField === 'guestEmail' }"
                                    :title="$t('Show on-screen keyboard')"
                                    @click.prevent.stop="toggleKeyboard('guestEmail')"
                                )
                                    i.el-icon-fa-keyboard.far
                            transition(name="slide-decent-vertical")
                                .keyboard-container(v-if="activeKeyboardField === 'guestEmail'")
                                    keyboard(
                                        ref="keyboardPanel"
                                        v-model="activeKeyboardValue"
                                        :config="keyboardConfig"
                                        @key-press="onKeyboardKeyPress"
                                    )

                        el-form-item(:label="$t('Party Size')" prop="partySize")
                            el-input-number(v-model="form.partySize" :min="1" :max="20" style="width: 100%" :disabled="!canEdit")
                            .preset-buttons(v-if="canEdit" :class="presetButtonClass")
                                el-button(
                                    v-for="n in 10"
                                    :key="n"
                                    :size="presetButtonSize"
                                    :type="form.partySize === n ? 'primary' : 'default'"
                                    @click="form.partySize = n"
                                ) {{ n }}

                    el-col(:span="12")
                        h3 {{ $t('Reservation Details') }}

                        el-form-item(:label="$t('Date')" prop="date")
                            el-date-picker(
                                v-model="form.date"
                                type="date"
                                :placeholder="$t('Select date')"
                                format="dd.MM.yyyy"
                                value-format="yyyy-MM-dd"
                                style="width: 100%"
                                :disabled="!canEdit"
                            )
                            .preset-buttons(v-if="canEdit" :class="presetButtonClass")
                                el-button(
                                    v-for="preset in datePresets"
                                    :key="preset.value"
                                    :size="presetButtonSize"
                                    :type="form.date === preset.value ? 'primary' : 'default'"
                                    @click="form.date = preset.value"
                                ) {{ preset.label }}

                        el-form-item(:label="$t('Time')" prop="time")
                            .preset-buttons(:class="presetButtonClass")
                                el-button(
                                    v-for="time in timePresets"
                                    :key="time"
                                    :size="presetButtonSize"
                                    :disabled="!canEdit"
                                    :type="form.time === time ? 'primary' : 'default'"
                                    @click="form.time = time"
                                ) {{ time }}

                        el-form-item(:label="$t('Duration')")
                            .preset-buttons(:class="presetButtonClass")
                                el-button(
                                    v-for="preset in durationPresets"
                                    :key="preset.value"
                                    :size="presetButtonSize"
                                    :disabled="!canEdit"
                                    :type="form.duration === preset.value ? 'primary' : 'default'"
                                    @click="form.duration = preset.value"
                                ) {{ preset.label }}

                        el-form-item(:label="$t('Table')")
                            el-select(
                                v-model="form.tableIds"
                                :placeholder="tablePlaceholder"
                                clearable
                                multiple
                                style="width: 100%"
                                :disabled="!canEdit"
                            )
                                el-option-group(
                                    v-for="room in groupedTables"
                                    :key="room.roomId"
                                    :label="room.roomName"
                                )
                                    el-option(
                                        v-for="table in room.tables"
                                        :key="table.id"
                                        :label="table.label"
                                        :value="table.id"
                                    )
                            .table-presets(v-if="canEdit")
                                .table-presets__group(v-for="group in tableGroups" :key="group.roomId")
                                    .table-presets__room(v-if="tableGroups.length > 1") {{ group.roomName }}
                                    .preset-buttons(:class="presetButtonClass")
                                        el-button(
                                            v-for="table in group.tables"
                                            :key="table.id"
                                            :size="presetButtonSize"
                                            :type="form.tableIds.indexOf(table.id) !== -1 ? 'primary' : 'default'"
                                            @click="toggleTable(table.id)"
                                        ) {{ table.label }}

                            .table-auto-hint(v-if="isAutomaticPlacement && !form.tableIds.length")
                                | {{ $t('Will be auto-assigned on save') }}

                el-divider

                el-row(:gutter="30")
                    el-col(:span="12")
                        el-form-item(:label="$t('Status')")
                            el-select(v-model="form.status" style="width: 100%" :disabled="!canEdit")
                                el-option(
                                    v-for="(label, key) in statusLabels"
                                    :key="key"
                                    :label="label"
                                    :value="key"
                                    :disabled="isStatusOptionDisabled(key)"
                                )

                        el-form-item(:label="$t('Special Requests')")
                            .preset-buttons.preset-buttons--before(v-if="canEdit && noteTags.length" :class="presetButtonClass")
                                el-button(
                                    v-for="tag in noteTags"
                                    :key="tag"
                                    :size="presetButtonSize"
                                    :type="hasNoteTag(tag) ? 'primary' : 'default'"
                                    @click="toggleNoteTag(tag)"
                                ) {{ tag }}

                            .input-with-keyboard.input-with-keyboard--textarea
                                el-input(
                                    v-model="form.notes"
                                    type="textarea"
                                    :rows="4"
                                    :placeholder="$t('Special requests, allergies, etc.')"
                                    :disabled="!canEdit"
                                    @focus="onFieldFocus('notes')"
                                )
                                button.keyboard-toggle(
                                    v-if="canEdit"
                                    type="button"
                                    :class="{ active: activeKeyboardField === 'notes' }"
                                    :title="$t('Show on-screen keyboard')"
                                    @click.prevent.stop="toggleKeyboard('notes')"
                                )
                                    i.el-icon-fa-keyboard.far
                            transition(name="slide-decent-vertical")
                                .keyboard-container(v-if="activeKeyboardField === 'notes'")
                                    keyboard(
                                        ref="keyboardPanel"
                                        v-model="activeKeyboardValue"
                                        :config="keyboardConfig"
                                        @key-press="onKeyboardKeyPress"
                                    )

                .form-actions
                    el-button(@click="goBack") {{ $t('Cancel') }}
                    el-button(
                        v-if="isEditing && canDelete"
                        type="danger"
                        @click="deleteReservation"
                    ) {{ $t('Delete') }}
                    el-button(v-if="canSave" type="primary" @click="save") {{ $t('Save') }}
</template>

<i18n>
{
    "de": {
        "New Reservation": "Neue Reservierung",
        "Edit Reservation": "Reservierung bearbeiten",
        "Guest Information": "Gastinformationen",
        "Guest Name": "Name des Gastes",
        "Phone": "Telefon",
        "Email": "E-Mail",
        "Party Size": "Personenanzahl",
        "Reservation Details": "Reservierungsdetails",
        "Date": "Datum",
        "Time": "Uhrzeit",
        "Duration": "Dauer",
        "Table": "Tisch",
        "Auto assign": "Automatisch zuweisen",
        "Select table(s)": "Tisch(e) wählen",
        "Status": "Status",
        "Special Requests": "Besondere Wünsche",
        "Special requests, allergies, etc.": "Besondere Wünsche, Allergien, etc.",
        "Save": "Speichern",
        "Delete": "Löschen",
        "Cancel": "Abbrechen",
        "Select date": "Datum wählen",
        "Select time": "Uhrzeit wählen",
        "Show on-screen keyboard": "Bildschirmtastatur einblenden",
        "Today": "Heute",
        "Tomorrow": "Morgen",
        "EE dd.MM.": "EE dd.MM.",
        "Reservation saved": "Reservierung gespeichert",
        "Reservation deleted": "Reservierung gelöscht",
        "Delete reservation?": "Reservierung löschen?",
        "This action cannot be undone.": "Diese Aktion kann nicht rückgängig gemacht werden.",
        "Will be auto-assigned on save": "Wird beim Speichern automatisch zugewiesen",
        "No matching table found – reservation is unassigned": "Kein passender Tisch gefunden – Reservierung ist unzugewiesen"
    }
}
</i18n>

<script>
import router from '@/router';
import { checkRestrictions } from '@/helpers';
import { useTableReservationStore, useTableReservationFloorPlanStore, useTableReservationSettingsStore } from '@/stores';
import { statusLabels } from '@/stores/tableReservation';
import { durationOptions, reservationTimeWindow } from '@/stores/tableReservationSettings';
import { isTerminalReservationStatus } from '@/extensions/tableReservation/store/utils';
import Keyboard from '@/components/Keyboard.vue';
import { addDays, format } from 'date-fns';
import { de } from 'date-fns/locale';

function toMinutes(time) {
    const parts = String(time || '').split(':');
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
}

function toTimeString(minutes) {
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return String(hours).padStart(2, '0') + ':' + String(rest).padStart(2, '0');
}

export default {
    name: 'ReservationForm',
    components: {
        Keyboard
    },
    data: function () {
        return {
            statusLabels: statusLabels,
            activeKeyboardField: null,
            // Without this simple-keyboard keeps writing at the caret position of the
            // real input, which never moves along and drops back to the start
            keyboardConfig: {
                disableCaretPositioning: true
            },
            numericKeyboardConfig: {
                disableCaretPositioning: true,
                layout: {
                    default: [
                        '1 2 3',
                        '4 5 6',
                        '7 8 9',
                        '+ 0 {backspace}'
                    ]
                }
            },
            rules: {
                guestName: 'required',
                partySize: 'required',
                date: 'required',
                time: 'required'
            },
        };
    },
    computed: {
        activeKeyboardValue: {
            get: function () {
                return this.activeKeyboardField ? this.form[this.activeKeyboardField] : '';
            },
            set: function (value) {
                if (this.activeKeyboardField) {
                    this.form[this.activeKeyboardField] = value;
                }
            }
        },
        store: function () {
            return useTableReservationStore();
        },
        floorPlanStore: function () {
            return useTableReservationFloorPlanStore();
        },
        settingsStore: function () {
            return useTableReservationSettingsStore();
        },
        datePresets: function () {
            const presets = [];

            for (let offset = 0; offset < 7; offset += 1) {
                const date = addDays(new Date(), offset);
                let label = format(date, this.$t('EE dd.MM.'), { locale: de });

                if (offset === 0) {
                    label = this.$t('Today');
                } else if (offset === 1) {
                    label = this.$t('Tomorrow');
                }

                presets.push({ value: format(date, 'yyyy-MM-dd'), label: label });
            }

            return presets;
        },
        timePresets: function () {
            const presets = [];
            const step = reservationTimeWindow.timelineSlotMinutes;
            const end = toMinutes(reservationTimeWindow.end);

            for (let minutes = toMinutes(reservationTimeWindow.start); minutes <= end; minutes += step) {
                presets.push(toTimeString(minutes));
            }

            return presets;
        },
        durationPresets: function () {
            return durationOptions.map((option) => {
                return {
                    value: option.value,
                    label: option.value < 60
                        ? option.value + ' Min'
                        : String(option.value / 60).replace('.', ',') + ' Std'
                };
            });
        },
        tableGroups: function () {
            return this.floorPlanStore.rooms.map((room) => {
                return {
                    roomId: room.id,
                    roomName: room.name,
                    tables: this.floorPlanStore.tables.filter((table) => {
                        return table.roomId === room.id;
                    }).map((table) => {
                        return {
                            id: table.id,
                            label: table.name || ('Tisch ' + table.number)
                        };
                    })
                };
            }).filter((group) => {
                return group.tables.length > 0;
            });
        },
        noteTags: function () {
            return this.settingsStore.settings.noteTags || [];
        },
        presetButtonSize: function () {
            const sizes = { default: 'mini', large: 'small', xlarge: 'medium' };
            return sizes[this.settingsStore.settings.presetButtonSize] || 'mini';
        },
        presetButtonClass: function () {
            return 'preset-buttons--' + (this.settingsStore.settings.presetButtonSize || 'default');
        },
        autoOpenKeyboard: function () {
            return !!this.settingsStore.settings.touchKeyboardAutoOpen;
        },
        isAutomaticPlacement: function () {
            return this.settingsStore.settings.tablePlacementMode === 'automatic';
        },
        tablePlaceholder: function () {
            if (this.isAutomaticPlacement) {
                return this.$t('Auto assign');
            }
            return this.$t('Select table(s)');
        },
        form: function () {
            return this.store.form;
        },
        isEditing: function () {
            return !!this.$route.params.id;
        },
        hasTerminalStatus: function () {
            return isTerminalReservationStatus(this.form.status);
        },
        canCreate: function () {
            return checkRestrictions(['ex:trsv:reservations'], 'create');
        },
        canUpdate: function () {
            return checkRestrictions(['ex:trsv:reservations'], 'update');
        },
        canDelete: function () {
            return checkRestrictions(['ex:trsv:reservations'], 'delete');
        },
        canEdit: function () {
            return this.isEditing ? this.canUpdate : this.canCreate;
        },
        canSave: function () {
            return this.canEdit;
        },
        groupedTables: function () {
            const tables = this.floorPlanStore.allTableOptions;
            const rooms = this.floorPlanStore.rooms;
            return rooms.map((room) => {
                return {
                    roomId: room.id,
                    roomName: room.name,
                    tables: tables.filter((t) => { return t.roomId === room.id; })
                };
            });
        }
    },
    mounted: async function () {
        const id = this.$route.params.id;
        try {
            await this.store.ensureBoard();

            if (id) {
                await this.store.fetchReservation(id);
            } else {
                this.store.resetForm();
            }
        } catch (error) {
            this.showActionError(error);
        }

        this.focusGuestName();
    },
    beforeDestroy: function () {
        this.store.resetForm();
    },
    methods: {
        focusGuestName: function () {
            if (!this.canEdit) {
                return;
            }

            this.$nextTick(() => {
                const input = this.$refs.guestNameInput;

                if (input) {
                    input.focus();
                }
            });
        },
        showActionError: function (error) {
            this.$message({
                message: error.message || this.$t('Something went wrong'),
                type: 'error',
                duration: 5000
            });
        },
        toggleTable: function (tableId) {
            const tableIds = (this.form.tableIds || []).slice();
            const index = tableIds.indexOf(tableId);

            if (index === -1) {
                tableIds.push(tableId);
            } else {
                tableIds.splice(index, 1);
            }

            this.form.tableIds = tableIds;
        },
        noteParts: function () {
            return String(this.form.notes || '')
                .split(',')
                .map((part) => { return part.trim(); })
                .filter(Boolean);
        },
        hasNoteTag: function (tag) {
            return this.noteParts().indexOf(tag) !== -1;
        },
        toggleNoteTag: function (tag) {
            const parts = this.noteParts();
            const index = parts.indexOf(tag);

            if (index === -1) {
                parts.push(tag);
            } else {
                parts.splice(index, 1);
            }

            this.form.notes = parts.join(', ');
        },
        onFieldFocus: function (field) {
            if (!this.autoOpenKeyboard || !this.canEdit || this.activeKeyboardField === field) {
                return;
            }

            this.openKeyboard(field);
        },
        toggleKeyboard: function (field) {
            if (this.activeKeyboardField === field) {
                this.activeKeyboardField = null;
                return;
            }

            this.openKeyboard(field);
        },
        openKeyboard: function (field) {
            this.activeKeyboardField = field;

            // The keyboard starts with an empty buffer, so existing text has to be handed over
            this.$nextTick(() => {
                const panel = this.$refs.keyboardPanel;

                if (panel && panel.keyboard) {
                    panel.keyboard.setInput(this.form[field] || '');
                }
            });
        },
        onKeyboardKeyPress: function (button) {
            if (button === '{enter}' || button === '{return}') {
                this.activeKeyboardField = null;
            }
        },
        goBack: function () {
            const query = this.$route.query.from === 'timeline' ? { tab: 'timeline' } : {};
            router.push({ name: 'table-reservations-overview', query: query });
        },
        isStatusOptionDisabled: function (status) {
            return this.hasTerminalStatus && status !== this.form.status;
        },
        save: function () {
            this.$refs.form.validate((valid) => {
                if (valid) {
                    this.store.saveReservation().then((result) => {
                        const autoAssignment = (result && result.autoAssignment) || { attempted: false, success: false };
                        if (autoAssignment.attempted && !autoAssignment.success) {
                            this.$message({
                                message: this.$t('No matching table found – reservation is unassigned'),
                                type: 'warning',
                                duration: 5000
                            });
                        } else {
                            this.$message({
                                message: this.$t('Reservation saved'),
                                type: 'success'
                            });
                        }
                        this.goBack();
                    }).catch((error) => {
                        this.showActionError(error);
                    });
                }
            });
        },
        deleteReservation: function () {
            this.$confirm(
                this.$t('This action cannot be undone.'),
                this.$t('Delete reservation?'),
                {
                    confirmButtonText: this.$t('Delete'),
                    cancelButtonText: this.$t('Cancel'),
                    type: 'warning'
                }
            ).then(() => {
                this.store.deleteReservation(this.form.id).then(() => {
                    this.$message({
                        message: this.$t('Reservation deleted'),
                        type: 'success'
                    });
                    this.goBack();
                }).catch((error) => {
                    this.showActionError(error);
                });
            }).catch(() => {});
        }
    }
};
</script>

<style lang="sass" scoped>
@import '~@/assets/sass/variables'

.input-with-keyboard
    position: relative
    display: flex
    align-items: center

    .el-input
        flex: 1

    &--textarea .keyboard-toggle
        top: 10px
        transform: none

.keyboard-toggle
    position: absolute
    right: 8px
    top: 50%
    transform: translateY(-50%)
    background: none
    border: none
    padding: 6px 8px
    cursor: pointer
    color: $color-grey-text
    font-size: 1.1em
    z-index: 1
    transition: color 0.2s

    &:hover,
    &.active
        color: $--color-primary

.keyboard-container
    margin-top: 8px
    background: darken($color-skeleton, 10%)
    border-radius: 8px
    padding: 12px

//- The shared keyboard sizes single keys for the full layout, the keypad needs even columns
.keyboard-container--numeric
    //- flex-basis 0 instead of the content width, otherwise "+" and the arrow key differ
    &::v-deep .simple-keyboard .hg-button
        width: auto
        flex: 1 1 0

.reservation-form
    padding: 16px 0 0 16px
    box-sizing: border-box

    h3
        margin-top: 0
        margin-bottom: 20px
        color: #303133

.preset-buttons
    margin-top: 8px
    display: flex
    gap: 4px
    flex-wrap: wrap

    &--before
        margin-top: 0
        margin-bottom: 10px

    .el-button
        min-width: 34px
        margin-left: 0
        padding: 4px 6px

    &--large
        gap: 6px

        .el-button
            min-width: 48px
            padding: 8px 12px
            font-size: 0.95rem

    &--xlarge
        gap: 8px

        .el-button
            min-width: 64px
            padding: 12px 18px
            font-size: 1.05rem

.table-presets
    &__group + &__group
        margin-top: 6px

    &__room
        margin-top: 8px
        font-size: 12px
        color: #909399

.table-auto-hint
    margin-top: 4px
    font-size: 12px
    color: #909399
    line-height: 1.4

.form-actions
    position: sticky
    bottom: 0
    z-index: 5
    display: flex
    justify-content: flex-end
    gap: 10px
    margin: 0 -20px -20px
    padding: 14px 20px
    background: #fff
    border-top: 1px solid #ebeef5

    .el-button
        margin-left: 0
</style>
